import { type EnumContainerActionRequest } from '@Model';
import { type ITableProperties } from '@synqo/frontend-component-library';
import { useCallback, useMemo, useState } from 'react';
import {
    canBeSelected,
    fitsSelection,
    followUpsAllowedFor,
    isGroupSelected,
    type SelectableGroup,
    type SelectableRow,
    stillListed,
    withTickedGroups,
    withTickedLines,
} from './containerSelection';

/** What a table selects its rows by. */
export type RowSelector = NonNullable<ITableProperties<SelectableRow>['objectSelector']>;

type TickedIds = string[] | ((current: string[]) => string[]);

type LineOf<TGroup extends SelectableGroup<SelectableRow>> = TGroup['lines'][number];

export interface ContainerSelection<TGroup extends SelectableGroup<SelectableRow>> {
    /** The ids of the lines that are selected, the first one selected first. */
    selected: ReadonlySet<string>;
    /** The lines that are selected, in the order they were selected. */
    selectedRows: readonly LineOf<TGroup>[];
    /** The follow-ups that every selected line allows. */
    allowedFollowUps: readonly EnumContainerActionRequest[];
    /** What the table of groups selects by: a group is ticked where all its lines are selected. */
    groupSelector: RowSelector;
    /** What the table of a group's lines selects by. */
    lineSelectorOf: (group: TGroup) => RowSelector;
    /** Deselects everything. */
    clear: () => void;
    /** Takes note of a page of groups: page 0 replaces the shown ones and deselects rows that are gone. */
    track: (groups: readonly TGroup[], pageNo: number) => void;
}

const resolved = (ticked: TickedIds, current: string[]) => (typeof ticked === 'function' ? ticked(current) : ticked);

/**
 * The lines of grouped container lists that are selected for a follow-up, which covers one customer at one site.
 *
 * @param locked Whether the selection may not be changed, which no row can then be ticked or unticked in.
 */
// @ai-generated 2026-10-05T10:28:57+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function useContainerSelection<TGroup extends SelectableGroup<SelectableRow>>(
    locked = false,
): ContainerSelection<TGroup> {
    const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
    const [groups, setGroups] = useState<readonly TGroup[]>([]);

    const rowsById = useMemo(
        () => new Map<string, LineOf<TGroup>>(groups.flatMap((group) => group.lines).map((line) => [line.id, line])),
        [groups],
    );

    const track = useCallback((shown: readonly TGroup[], pageNo: number) => {
        if (pageNo > 0) {
            setGroups((current) => [...current, ...shown]);
            return;
        }

        setGroups(shown);
        const shownRows = new Map(shown.flatMap((group) => group.lines).map((line) => [line.id, line]));
        setSelected((current) => stillListed(current, shownRows));
    }, []);

    const allowedFollowUps = useMemo(() => followUpsAllowedFor(selected, rowsById), [selected, rowsById]);

    const selectedRows = useMemo(() => [...selected].flatMap((id) => rowsById.get(id) ?? []), [selected, rowsById]);

    const clear = useCallback(() => setSelected(new Set()), []);

    const groupSelector = useMemo<RowSelector>(
        () => ({
            selectedObjectIds: groups.filter((group) => isGroupSelected(group, selected)).map((group) => group.id),
            setSelectedObjectIds: (ticked: TickedIds) =>
                setSelected((current) =>
                    withTickedGroups(
                        current,
                        groups,
                        resolved(
                            ticked,
                            groups.filter((group) => isGroupSelected(group, current)).map((group) => group.id),
                        ),
                    ),
                ),
            locked: (groupId) => {
                const group = groups.find((candidate) => candidate.id === groupId);

                return (
                    locked ||
                    group === undefined ||
                    !group.lines.some(canBeSelected) ||
                    !fitsSelection(group, selected, rowsById)
                );
            },
        }),
        [groups, selected, rowsById, locked],
    );

    const lineSelectorOf = useCallback(
        (group: TGroup): RowSelector => ({
            selectedObjectIds: group.lines.filter((line) => selected.has(line.id)).map((line) => line.id),
            setSelectedObjectIds: (ticked: TickedIds) =>
                setSelected((current) =>
                    withTickedLines(
                        current,
                        group,
                        resolved(
                            ticked,
                            group.lines.filter((line) => current.has(line.id)).map((line) => line.id),
                        ),
                    ),
                ),
            locked: (lineId) => {
                const line = group.lines.find((candidate) => candidate.id === lineId);

                return (
                    locked || line === undefined || !canBeSelected(line) || !fitsSelection(group, selected, rowsById)
                );
            },
        }),
        [selected, rowsById, locked],
    );

    return useMemo(
        () => ({ selected, selectedRows, allowedFollowUps, groupSelector, lineSelectorOf, clear, track }),
        [selected, selectedRows, allowedFollowUps, groupSelector, lineSelectorOf, clear, track],
    );
}
